import { CoinsIcon, InfoIcon, XCircleIcon } from 'lucide-react'

import TiltedCard from '@/common/TiltedCard'
import {
  formatAtomicCompact,
  formatBridgeAtomic,
  formatUtc,
  getNftFullUnlockBlock,
  getNftUnlockPercent,
  isMobile,
  isNftFullyUnlocked,
} from '@/helpers'
import { cn } from '@/theme/utils'
import { Nft } from '@/types'
import UiAddressExplorerCopy from '@/ui/UiAddressExplorerCopy'
import { UiButton } from '@/ui/UiButton'
import { UiSeparator } from '@/ui/UiSeparator'
import { UiSkeleton } from '@/ui/UiSkeleton'

import { useNftMetadata } from '../hooks/nft-metadata'
import { NftActionsDropdown } from './NftActionsDropdown'

type Props = {
  nft: Nft
  currentBlock: number
  vestingPeriodBlocks: number

  delegation: {
    validatorAddress: string
    amountAtomic: string
    timestamp?: string
  } | null

  isDelegationLoading: boolean
}

export function NftDetailsModalContent({
  currentBlock,
  vestingPeriodBlocks,
  nft,
  delegation,
  isDelegationLoading,
}: Props) {
  return (
    <div className='text-foreground'>
      <div className='flex min-w-0 flex-col gap-5'>
        <MetadataDetails uri={nft.uri} />

        <AddressBlock nft={nft} />

        <UiSeparator />

        <RewardsBlock nft={nft} />

        <UiSeparator />

        <VestingBlock
          nft={nft}
          currentBlock={currentBlock}
          vestingPeriodBlocks={vestingPeriodBlocks}
        />

        <UiSeparator />

        <div className='space-y-3'>
          <div className='text-muted-foreground text-xs'>Delegation</div>
          <DelegationBlock
            isLoading={isDelegationLoading}
            delegation={delegation}
          />
        </div>
      </div>
    </div>
  )
}

// TODO: fetch real metadata
function MetadataDetails({ uri: _uri }: { uri: string }) {
  const metadata = {
    attributes: [
      { trait_type: 'Bonus', value: '20% bonus' },
      { trait_type: 'Unlock', value: '1-year unlock' },
    ],
  }

  if (!metadata) return null
  return (
    <div className='space-y-2'>
      {metadata.attributes && metadata.attributes.length > 0 && (
        <div>
          <span className='text-muted-foreground text-xs'>Attributes</span>
          <div className='mt-2 flex flex-wrap gap-2'>
            {metadata.attributes.map((attr, i) => (
              <div
                key={`${attr.trait_type ?? 'attr'}-${i}`}
                className={cn(
                  'bg-muted/20 border-border/60 text-foreground inline-flex items-center rounded-sm border px-3 py-1 text-sm font-semibold',
                )}
              >
                <span>{attr.value ?? '-'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function NftHeader({
  uri,
  vestingTimeLabel,
  canTopUp,
  canClose,
  availableToWithdrawAtomic,
  nftBalanceAtomic,
  onTopUp,
  onCloseShard,
  actions,

  onDelegate,
  onUndelegate,
  onRedelegate,
  onWithdraw,
  onChangeOwner,
}: {
  uri: string
  vestingTimeLabel?: string | null
  canTopUp?: boolean
  canClose?: boolean
  availableToWithdrawAtomic: string
  nftBalanceAtomic: string

  actions: {
    visible: boolean
    canDelegate?: boolean
    canWithdraw?: boolean
    hasDelegation?: boolean
  }

  onTopUp(): void
  onCloseShard(): void
  onDelegate(): void
  onUndelegate(): void
  onRedelegate(): void
  onWithdraw(): void
  onChangeOwner(): void
}) {
  const { data: metadata, isLoading } = useNftMetadata(uri)
  const mobile = isMobile()

  const placeholderImageSrc = `${import.meta.env.BASE_URL}images/nft-preview.png`

  // Only one image is allowed for now
  const previewImageSrc = placeholderImageSrc
  const title = metadata?.name ?? `Unknown NFT`
  const description = metadata?.description ?? 'No description provided.'

  if (mobile) {
    return (
      <div className='mb-2 flex flex-col gap-4'>
        <div className='flex w-full flex-col items-center gap-3'>
          <div className='w-[84px]'>
            <div className='aspect-square w-full'>
              <TiltedCard
                imageSrc={previewImageSrc}
                altText={title}
                containerHeight='100%'
                containerWidth='100%'
                imageHeight='100%'
                imageWidth='100%'
                scaleOnHover={1.03}
                rotateAmplitude={10}
                showMobileWarning={false}
                showTooltip={false}
              />
            </div>
          </div>

          <div className='w-full min-w-0 px-3 text-center'>
            {isLoading ? (
              <UiSkeleton className='mx-auto h-5 w-40 rounded-md' />
            ) : (
              <div
                title={title}
                className='mx-auto line-clamp-2 max-w-full text-base font-semibold'
              >
                {title}
              </div>
            )}

            <div className='mt-2 flex items-center justify-center gap-2'>
              {vestingTimeLabel && (
                <div
                  className={cn(
                    'chip-gradient inline-flex h-6 items-center rounded-full px-2',
                    'text-foreground text-xs font-semibold backdrop-blur-md',
                  )}
                >
                  {vestingTimeLabel}
                </div>
              )}
              {vestingTimeLabel && (
                <div
                  className={cn(
                    'chip-gradient-teal inline-flex h-6 items-center rounded-full px-2',
                    'text-foreground text-xs font-semibold backdrop-blur-md',
                  )}
                >
                  +20%
                </div>
              )}
            </div>

            {isLoading ? (
              <div className='mt-3 flex flex-col items-center gap-2'>
                <UiSkeleton className='h-4 w-5/6 rounded-md' />
                <UiSkeleton className='h-4 w-2/3 rounded-md' />
              </div>
            ) : (
              <p
                title={description}
                className='text-foreground mt-3 line-clamp-4 text-sm font-normal whitespace-pre-line'
              >
                {description}
              </p>
            )}

            <div className='mt-4 space-y-2 text-sm'>
              <div className='flex flex-col gap-2'>
                <div className='text-muted-foreground text-sm font-normal'>
                  Available to Withdraw:
                </div>
                <div className='truncate text-sm font-semibold text-[#16D1D6]'>
                  {formatBridgeAtomic(availableToWithdrawAtomic)} BRIDGE
                </div>
              </div>

              <div className='flex flex-col gap-2'>
                <div className='text-muted-foreground text-sm font-normal'>
                  Shard Balance:
                </div>
                <div className='text-sm font-semibold'>
                  {formatBridgeAtomic(nftBalanceAtomic)} BRIDGE
                </div>
              </div>

              <div className='mt-4 flex flex-wrap items-center justify-center gap-2'>
                {canTopUp && (
                  <UiButton
                    size='sm'
                    variant='white'
                    className='rounded-full px-3 text-xs'
                    onClick={onTopUp}
                  >
                    <CoinsIcon />
                    Top up
                  </UiButton>
                )}

                {canClose && (
                  <UiButton
                    size='sm'
                    variant='white'
                    className='rounded-full px-3 text-xs'
                    onClick={onCloseShard}
                  >
                    <XCircleIcon />
                    Close Shard
                  </UiButton>
                )}

                {actions.visible && (
                  <div className='flex shrink-0 items-center'>
                    <NftActionsDropdown
                      actions={actions}
                      onDelegate={onDelegate}
                      onUndelegate={onUndelegate}
                      onRedelegate={onRedelegate}
                      onWithdraw={onWithdraw}
                      onChangeOwner={onChangeOwner}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='mb-2 flex flex-col gap-4 rounded-t-2xl sm:items-start'>
      <div className='flex w-full flex-col gap-4 sm:flex-row'>
        <div className='mx-auto mt-2 w-[100px] sm:w-58'>
          <div className='aspect-square w-full sm:mt-3'>
            <TiltedCard
              imageSrc={previewImageSrc}
              altText={title}
              containerHeight='100%'
              containerWidth='100%'
              imageHeight='100%'
              imageWidth='100%'
              scaleOnHover={1.03}
              rotateAmplitude={10}
              showMobileWarning={false}
              showTooltip={false}
            />
          </div>
        </div>

        <div className='min-w-0 flex-1 space-y-2 pt-1 md:mt-8'>
          <div className='flex w-full items-start gap-2'>
            {isLoading ? (
              <UiSkeleton className='h-5 w-40 rounded-md' />
            ) : (
              <div
                title={title}
                className='line-clamp-2 w-fit max-w-[180px] text-base font-semibold wrap-break-word'
              >
                {title}
              </div>
            )}
            {vestingTimeLabel && (
              <div className='flex items-center gap-2'>
                <div
                  className={cn(
                    'shrink-0',
                    'chip-gradient inline-flex h-5 items-center rounded-full px-2',
                    'text-foreground text-xs font-semibold backdrop-blur-md',
                  )}
                >
                  {vestingTimeLabel}
                </div>
                <div
                  className={cn(
                    'shrink-0',
                    'chip-gradient-teal inline-flex h-5 items-center rounded-full px-2',
                    'text-foreground text-xs font-semibold backdrop-blur-md',
                  )}
                >
                  +20%
                </div>
              </div>
            )}
          </div>
          {isLoading ? (
            <div className='space-y-2'>
              <UiSkeleton className='h-4 w-5/6 rounded-md' />
              <UiSkeleton className='h-4 w-2/3 rounded-md' />
            </div>
          ) : (
            <p
              title={description}
              className='text-foreground line-clamp-4 truncate text-sm font-normal wrap-break-word whitespace-pre-line'
            >
              {description}
            </p>
          )}

          <div className='mt-4 space-y-2 text-sm'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='text-muted-foreground text-sm font-normal'>
                Available to Withdraw:
              </div>
              <div className='truncate text-sm font-semibold text-[#16D1D6]'>
                {formatBridgeAtomic(availableToWithdrawAtomic)} BRIDGE
              </div>
            </div>

            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='text-muted-foreground text-sm font-normal'>
                Shard Balance:
              </div>
              <div className='flex items-center gap-2'>
                <div className='text-sm font-semibold'>
                  {formatBridgeAtomic(nftBalanceAtomic)} BRIDGE
                </div>
              </div>
            </div>

            <div className='mt-6 flex flex-wrap gap-2'>
              {canTopUp && (
                <UiButton
                  size='sm'
                  variant='white'
                  className='rounded-full px-3 text-xs'
                  onClick={onTopUp}
                >
                  <CoinsIcon />
                  Top up
                </UiButton>
              )}

              {canClose && (
                <UiButton
                  size='sm'
                  variant='white'
                  className='rounded-full px-3 text-xs'
                  onClick={onCloseShard}
                >
                  <XCircleIcon />
                  Close Shard
                </UiButton>
              )}

              {actions.visible && (
                <div className='flex shrink-0 items-center'>
                  <NftActionsDropdown
                    actions={actions}
                    onDelegate={onDelegate}
                    onUndelegate={onUndelegate}
                    onRedelegate={onRedelegate}
                    onWithdraw={onWithdraw}
                    onChangeOwner={onChangeOwner}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function AddressBlock({ nft }: { nft: Nft }) {
  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <span className='text-foreground text-sm'>Address:</span>
        <div className='font-medium'>
          <span className='inline-flex items-center gap-2 rounded-full px-3'>
            <UiAddressExplorerCopy
              linkClassName='text-primary'
              start={20}
              end={10}
              address={nft.address}
            />
          </span>
        </div>
      </div>

      <UiSeparator />

      <div className='flex flex-wrap items-center justify-between gap-2'>
        <span className='text-foreground text-sm'>Owner:</span>
        <div className='font-medium'>
          <span className='inline-flex items-center gap-2 rounded-full px-3'>
            <UiAddressExplorerCopy
              linkClassName='text-primary'
              start={20}
              end={10}
              address={nft.owner}
            />
          </span>
        </div>
      </div>
    </div>
  )
}

function RewardsBlock({ nft }: { nft: Nft }) {
  return (
    <div className='flex flex-wrap items-center justify-between gap-2'>
      <div className='text-foreground text-sm'>Rewards:</div>
      <div className='text-sm font-semibold'>
        Reward / period: {formatAtomicCompact(nft.reward_per_period.amount)}{' '}
        BRIDGE
      </div>
    </div>
  )
}

function VestingBlock({
  nft,
  currentBlock,
  vestingPeriodBlocks,
}: {
  nft: Nft
  currentBlock: number
  vestingPeriodBlocks: number
}) {
  const isFullyUnlocked = isNftFullyUnlocked(nft)
  const fullUnlockBlock = getNftFullUnlockBlock(nft, vestingPeriodBlocks)
  const lastUnlockBlock = Number(nft.last_vesting_block)

  return (
    <div>
      <div className='text-muted-foreground mb-2 text-xs'>Vesting</div>
      <div className='text-foreground flex items-start gap-2 rounded-md bg-[#141622] p-4 text-xs'>
        <InfoIcon className='mt-0.5 size-4 shrink-0' />
        <div>
          Tokens of this Shard are unlocked gradually in fixed periods, not all
          at once. Before the full unlock you can withdraw only the unlocked
          part.
          <br />
          Staking rewards and other transfers to the Shard become available for
          withdrawal after the 100% unlock.
        </div>
      </div>

      <div className='mt-4 flex flex-col gap-5 text-sm tabular-nums'>
        <div className='flex items-center justify-between gap-5'>
          <span className='text-foreground'>Unlocked:</span>
          <span className='text-right font-semibold'>
            {formatAtomicCompact(nft.unlocked_amount.amount)} /{' '}
            {formatAtomicCompact(nft.token_amount.amount)} BRIDGE (
            {getNftUnlockPercent(nft)}%)
          </span>
        </div>
        <UiSeparator />
        <div className='flex items-center justify-between gap-5'>
          <span className='text-foreground'>Withdrawn:</span>
          <span className='font-semibold'>
            {formatAtomicCompact(nft.withdrawn_amount.amount)} BRIDGE
          </span>
        </div>
        <UiSeparator />
        <div className='flex items-center justify-between gap-4'>
          <span className='text-foreground'>Unlock starts at block:</span>
          <span className='font-semibold'>{nft.start_vesting_block}</span>
        </div>
        <UiSeparator />
        <div className='flex items-center justify-between gap-4'>
          <span className='text-foreground'>Last unlock at block:</span>
          <span className='font-semibold'>
            {lastUnlockBlock > 0 ? lastUnlockBlock : '—'}
          </span>
        </div>
        <UiSeparator />
        <div className='flex items-center justify-between gap-4'>
          <span className='text-foreground'>Full unlock at block:</span>
          <span className='font-semibold'>
            {isFullyUnlocked ? 'Fully unlocked' : `~${fullUnlockBlock ?? '—'}`}
          </span>
        </div>
        <UiSeparator />
        <div className='flex items-center justify-between gap-4'>
          <span className='text-foreground'>Current block:</span>
          <span className='font-semibold'>{currentBlock}</span>
        </div>
      </div>
    </div>
  )
}

function DelegationBlock({
  isLoading,
  delegation,
}: {
  isLoading: boolean
  delegation: {
    validatorAddress: string
    amountAtomic: string
    timestamp?: string
  } | null
}) {
  if (isLoading) {
    return <div className='muted-foreground text-sm'>Checking delegation…</div>
  }

  if (!delegation) {
    return <div className='muted-foreground text-sm'>Not delegated</div>
  }

  return (
    <div className='flex flex-col gap-5 text-sm'>
      <div className='flex items-center justify-between gap-4'>
        <span className='text-foreground'>Amount:</span>
        <strong className='ml-1 tabular-nums'>
          {formatBridgeAtomic(delegation.amountAtomic)} BRIDGE
        </strong>
      </div>

      <UiSeparator />

      {delegation.timestamp && (
        <div className='flex items-center justify-between gap-4'>
          <span className='text-foreground'>Since:</span>
          <strong className='ml-1 tabular-nums'>
            {`${formatUtc(new Date(delegation.timestamp))} UTC`}
          </strong>
        </div>
      )}

      <UiSeparator />

      <div className='flex flex-wrap items-start justify-between gap-2'>
        <span className='text-foreground'>Validator:</span>
        <span className='inline-flex items-center gap-2 rounded-full px-3 py-1'>
          <UiAddressExplorerCopy
            variant='validator'
            linkClassName='text-primary'
            start={20}
            end={10}
            address={delegation.validatorAddress}
          />
        </span>
      </div>
    </div>
  )
}
